/**
 * High-Performance Render Pipeline
 * Manages viewport size, DPR scaling, point-light uniform illumination, and zero-GC draw passes.
 */

import { MemoryPool } from '../core/MemoryPool';
import { CameraState } from './CameraSpline';

export class RenderPipeline {
  private canvas: HTMLCanvasElement;
  private ctx: CanvasRenderingContext2D;
  private dpr: number = 1;
  private width: number = 0;
  private height: number = 0;

  private memoryPool: MemoryPool;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const context = canvas.getContext('2d', { alpha: false, desynchronized: true });
    if (!context) {
      throw new Error('Failed to obtain Canvas2D context');
    }
    this.ctx = context;
    this.memoryPool = MemoryPool.getInstance();
    this.resize();
  }

  public resize(): void {
    this.dpr = Math.min(window.devicePixelRatio || 1, 2.0); // Cap DPR at 2 for locked 60+ FPS
    this.width = window.innerWidth;
    this.height = window.innerHeight;

    this.canvas.width = Math.floor(this.width * this.dpr);
    this.canvas.height = Math.floor(this.height * this.dpr);
    this.canvas.style.width = `${this.width}px`;
    this.canvas.style.height = `${this.height}px`;

    this.ctx.scale(this.dpr, this.dpr);
  }

  public clear(): void {
    this.ctx.fillStyle = '#0d0b0a'; // Primordial slate dark black
    this.ctx.fillRect(0, 0, this.width, this.height);
  }

  /**
   * Renders primordial slate cavern background with dynamic ember point light illumination
   */
  public renderCavernBackground(
    lightX: number,
    lightY: number,
    lightIntensity: number,
    sceneId: number
  ): void {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    // Convert normalized [-1, 1] light pos to screen pixels
    const px = ((lightX + 1) / 2) * w;
    const py = ((-lightY + 1) / 2) * h;

    // Dark base cavern gradient
    const baseGradient = ctx.createRadialGradient(
      px, py, 10,
      px, py, Math.max(w, h) * 0.8
    );

    if (sceneId === 0) {
      // Scene 01: Primordial Slate Cavern
      const activeIntensity = Math.max(0.35, lightIntensity);
      const r = Math.floor(226 * activeIntensity * 0.9);
      const g = Math.floor(91 * activeIntensity * 0.7);
      const b = Math.floor(45 * activeIntensity * 0.4);

      baseGradient.addColorStop(0, `rgba(${r}, ${g}, ${b}, ${0.5 + activeIntensity * 0.4})`);
      baseGradient.addColorStop(0.4, `rgba(${Math.floor(r * 0.5)}, ${Math.floor(g * 0.4)}, ${Math.floor(b * 0.3)}, 0.35)`);
      baseGradient.addColorStop(1, '#080706');

      ctx.fillStyle = baseGradient;
      ctx.fillRect(0, 0, w, h);

      // Render Chiseled Relief Carving
      ctx.save();
      ctx.globalAlpha = Math.min(1.0, 0.6 + activeIntensity * 0.4);
      ctx.font = '700 15px "Inter", "Outfit", monospace';
      ctx.textAlign = 'center';
      ctx.letterSpacing = '6px';
      ctx.fillStyle = 'rgba(240, 220, 200, 0.75)';
      ctx.shadowColor = 'rgba(226, 91, 45, 0.8)';
      ctx.shadowBlur = 15;

      ctx.fillText('ALEXANDER VEX  //  ARCHITECT OF DIGITAL WORLDS', w / 2, h / 2 - 20);

      ctx.font = '400 11px monospace';
      ctx.fillStyle = 'rgba(210, 190, 170, 0.5)';
      ctx.fillText('│││││  │││││  │││││  │││││  [PRIMORDIAL IGNITION TALLY]', w / 2, h / 2 + 20);
      ctx.restore();
    } else {
      // General Era Backgrounds
      ctx.fillStyle = '#0b0c10';
      ctx.fillRect(0, 0, w, h);
    }
  }

  /**
   * Zero-GC rendering pass for memory pool particle embers
   */
  public renderParticles(_camera: CameraState): void {
    const ctx = this.ctx;
    const buf = this.memoryPool.particleBuffer;
    const count = this.memoryPool.activeParticleCount;
    const stride = MemoryPool.FLOATS_PER_PARTICLE;

    const w = this.width;
    const h = this.height;

    ctx.save();
    ctx.globalCompositeOperation = 'lighter'; // Additive bloom blending

    for (let i = 0; i < count; i++) {
      const idx = i * stride;

      const x = buf[idx + 0];
      const y = buf[idx + 1];

      // Screen space conversion
      const sx = ((x + 1) / 2) * w;
      const sy = ((-y + 1) / 2) * h;

      const size = buf[idx + 8];
      const r = Math.floor(buf[idx + 9] * 255);
      const g = Math.floor(buf[idx + 10] * 255);
      const b = Math.floor(buf[idx + 11] * 255);
      const a = buf[idx + 12];

      if (a <= 0.01) continue;

      ctx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a.toFixed(2)})`;
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(1, size), 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.restore();
  }

  /**
   * Floating atmospheric microscopic dust particles
   */
  public renderAmbientDust(time: number): void {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    ctx.save();
    ctx.fillStyle = 'rgba(255, 240, 220, 0.08)';

    for (let i = 0; i < 40; i++) {
      const x = (Math.sin(i * 99 + time * 0.2) * 0.5 + 0.5) * w;
      const y = (Math.cos(i * 33 + time * 0.15) * 0.5 + 0.5) * h;
      const radius = 1 + (i % 3);

      ctx.beginPath();
      ctx.arc(x, y, radius, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  /**
   * ACES Vignette & Tonemapping overlay pass
   */
  public renderVignette(): void {
    const ctx = this.ctx;
    const w = this.width;
    const h = this.height;

    const grad = ctx.createRadialGradient(
      w / 2, h / 2, Math.min(w, h) * 0.4,
      w / 2, h / 2, Math.max(w, h) * 0.8
    );

    grad.addColorStop(0, 'rgba(0,0,0,0)');
    grad.addColorStop(1, 'rgba(5,3,2,0.85)');

    ctx.fillStyle = grad;
    ctx.fillRect(0, 0, w, h);
  }
}
